// ═══════════════════════════════════════════════════════════════════════
// CONNECTOR ICON — pure resolver (framework-free)
// ═══════════════════════════════════════════════════════════════════════
// Lives apart from the React component so the no-emoji guarantee can be
// unit-tested in node with zero deps. Mirrored verbatim in apps/build (the
// build-preview copy of IntegrationHub). The Integration screen must NEVER
// render a raw emoji again — a real http(s) URL becomes a self-hosted logo
// <img>; anything else (empty, a legacy emoji, the old 🔌, junk) becomes a
// clean monogram tile.

export type ResolvedConnectorIcon =
  | { mode: "img"; src: string }
  | { mode: "monogram"; initials: string };

export const DEFAULT_CONNECTOR_COLOR = "#6366F1";

const ICON_PROXY_ROUTE = "/api/connector-icon";

function faviconLogo(domain: string): string {
  return `https://www.google.com/s2/favicons?sz=128&domain=${encodeURIComponent(domain)}`;
}

const FALLBACK_LOGO_BY_NAME: Record<string, string> = {
  gmail: faviconLogo("google.com"),
  "google workspace": faviconLogo("google.com"),
  "google drive": faviconLogo("google.com"),
  "google sheets": faviconLogo("google.com"),
  "google calendar": faviconLogo("google.com"),
  linkedin: faviconLogo("linkedin.com"),
  instagram: faviconLogo("instagram.com"),
  whatsapp: faviconLogo("whatsapp.com"),
  "facebook pages": faviconLogo("facebook.com"),
  "facebook lead ads": faviconLogo("facebook.com"),
  youtube: faviconLogo("youtube.com"),
  tiktok: faviconLogo("tiktok.com"),
  pinterest: faviconLogo("pinterest.com"),
  reddit: faviconLogo("reddit.com"),
  threads: faviconLogo("threads.net"),
  slack: faviconLogo("slack.com"),
  hubspot: faviconLogo("hubspot.com"),
  salesforce: faviconLogo("salesforce.com"),
  notion: faviconLogo("notion.so"),
  airtable: faviconLogo("airtable.com"),
  stripe: faviconLogo("stripe.com"),
  github: faviconLogo("github.com"),
  gitlab: faviconLogo("gitlab.com"),
};

// 1–2 uppercase initials from the provider's display name. Derived ONLY from
// `name`, never from `icon`, so a legacy emoji (e.g. 🔌) in `icon` can never
// reach the DOM — it always falls through to a monogram.
export function monogramInitials(name: string): string {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function isRenderableIconSource(src: string): boolean {
  return /^https?:\/\//i.test(src) || /^data:image\//i.test(src) || src.startsWith(ICON_PROXY_ROUTE);
}

function proxiedConnectorIcon(src: string, name: string): string {
  if (src.startsWith(ICON_PROXY_ROUTE)) return src;
  return `${ICON_PROXY_ROUTE}?src=${encodeURIComponent(src)}&name=${encodeURIComponent(name)}`;
}

// Precedence: a real http(s) URL → <img>; anything else → monogram.
export function resolveConnectorIcon(args: { icon?: string | null; name: string }): ResolvedConnectorIcon {
  const icon = String(args.icon ?? "").trim();
  if (isRenderableIconSource(icon)) return { mode: "img", src: proxiedConnectorIcon(icon, args.name) };
  const fallback = FALLBACK_LOGO_BY_NAME[String(args.name || "").trim().toLowerCase()];
  if (fallback) return { mode: "img", src: proxiedConnectorIcon(fallback, args.name) };
  return { mode: "monogram", initials: monogramInitials(args.name) };
}
